import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { Session, AuthError, PostgrestError } from '@supabase/supabase-js';
import { canEditSection as checkSectionPermission } from '../config/auth';
import { logger } from '../utils/logger';

type Role = 'admin' | 'viewer' | null;
type User = { email: string | null };

/**
 * UserContext provides global authentication state and user management
 * 
 * Key features:
 * - Authentication state management (user, role, session)
 * - Role-based access control (admin/viewer)
 * - Viewer mode toggle for admins to test viewer experience
 * - Magic link authentication support
 * - Session persistence across page refreshes
 */
type UserContextType = {
  user: User | null;
  role: Role;
  isAdmin: boolean;
  isActuallyAdmin: boolean;
  viewerMode: boolean;
  canEdit: boolean;
  workspaceId: string;
  authReady: boolean;
  userId: string | null;
  viewAsViewer: () => void;
  backToAdmin: () => void;
  canEditSection: (section: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<{ data: { user: unknown } | null; error: AuthError | null }>;
  fetchUserRole: (userId: string) => Promise<{ data: { role: string } | null; error: PostgrestError | null }>;
  setViewerMode: (v: boolean) => void;
  userMode: 'admin' | 'viewer';
  switchUserMode: (mode: 'admin' | 'viewer') => void;
  viewerId: string;
};

export const UserContext = createContext<UserContextType | undefined>(undefined);

/**
 * Normalizes role value to 'admin' | 'viewer' | null
 * Handles various input types and string casing
 */
const normalizeRole = (v: unknown): Role => {
  if (v === null || v === undefined || v === '') return null;
  return (typeof v === 'string' && v.toLowerCase() === 'admin') ? 'admin' : 'viewer';
};

/**
 * Extracts role from Supabase session JWT
 * Priority: user_metadata.role > app_metadata.role
 */
const getRoleFromSession = (session: Session | null): Role => {
  if (!session?.user) return null;
  const metaRole = session.user.user_metadata?.role;
  const appRole = session.user.app_metadata?.role;
  return normalizeRole(metaRole ?? appRole);
};

/**
 * Extracts workspace ID from session or uses default
 */
const getWorkspaceIdFromSession = (session: Session | null): string => {
  if (!session?.user) {
    const envWorkspaceId = import.meta.env.VITE_DEFAULT_WORKSPACE_ID;
    return envWorkspaceId || '00000000-0000-0000-0000-000000000001';
  }
  const appWorkspaceId = session.user.app_metadata?.workspace_id;
  const envWorkspaceId = import.meta.env.VITE_DEFAULT_WORKSPACE_ID;
  return (appWorkspaceId as string) || envWorkspaceId || '00000000-0000-0000-0000-000000000001';
};

const ROLE_KEY = 'auth:role';
const USER_ID_KEY = 'auth:userId';
const VIEW_AS_VIEWER_KEY = 'ui:viewerMode';

const isDev = !!import.meta.env.DEV;
const log: Pick<Console,'debug'|'info'|'warn'|'error'> = isDev ? console : {debug(){},info(){},warn(){},error(){}};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<{
    user: User | null;
    role: Role;
    session: Session | null;
    loading: boolean;
  }>({
    user: null,
    role: 'viewer',
    session: null,
    loading: true
  });

  /**
   * Viewer mode allows admins to temporarily view the app as viewers
   * Persisted in localStorage to survive page refreshes
   */
  const [viewerMode, setViewerModeState] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(VIEW_AS_VIEWER_KEY);
      const result = raw === 'true';
      log.debug('[UserContext] Initial viewer mode state:', { raw, result });
      return result;
      } catch (error) {
        logger.error('Error reading viewer mode from localStorage', { error }, 'UserContext');
        return false;
      }
  });

  /**
   * Auto-disable viewer mode if user is not admin
   * Prevents non-admins from viewing as viewer
   */
  useEffect(() => {
    setViewerModeState(prev => {
      const canMasquerade = state.role === 'admin';
      const next = canMasquerade ? prev : false;
      try {
        localStorage.setItem(VIEW_AS_VIEWER_KEY, String(next));
      } catch (error) {
        // localStorage may be unavailable (private browsing, quota exceeded)
        if (import.meta.env.DEV) {
          logger.warn('Failed to set viewer mode in localStorage', { error }, 'UserContext');
        }
      }
      return next;
    });
  }, [state.role, state.user?.email]);

  /**
   * Effective role: viewer mode overrides actual role for admins
   * Returns null if user has no role (pending approval)
   */
  const role = useMemo(() => {
    if (state.role === null) return null;
    return viewerMode ? 'viewer' : state.role;
  }, [viewerMode, state.role]);

  const isAdmin = role === 'admin';
  const isActuallyAdmin = state.role === 'admin';
  const canEdit = state.role === 'admin' && viewerMode === false;

  /**
   * Enable viewer mode for admins
   * Allows admins to test the viewer experience
   */
  const viewAsViewer = useCallback(() => {
    if (state.role !== 'admin') return;
    try {
      localStorage.setItem(VIEW_AS_VIEWER_KEY, 'true');
    } catch (error) {
      logger.error('Failed to set viewer mode in localStorage', { error }, 'UserContext');
    }
    setViewerModeState(true);
  }, [state.role]);

  /**
   * Disable viewer mode, return to admin view
   */
  const backToAdmin = useCallback(() => {
    if (state.role !== 'admin') return;
    try {
      localStorage.setItem(VIEW_AS_VIEWER_KEY, 'false');
    } catch (error) {
      logger.error('Failed to clear viewer mode in localStorage', { error }, 'UserContext');
    }
    setViewerModeState(false);
  }, [state.role]);

  /**
   * Checks if user can edit a specific section
   * Uses effective role (viewer mode affects permissions)
   */
  const canEditSection = useCallback((section: string): boolean => {
    const effectiveRole = viewerMode ? 'viewer' : (state.role || 'viewer');
    return checkSectionPermission(effectiveRole as 'admin' | 'viewer', section);
  }, [state.role, viewerMode]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      log.debug('[UserContext] login successful', { email });
    } catch (error) {
      log.error('Login failed:', error);
      throw error;
    }
  }, []);

  /**
   * Logout: Clears local session, localStorage, and Supabase session
   * Ensures clean logout even if Supabase signOut fails
   */
  const logout = useCallback(async () => {
    try {
      setState(prev => ({ 
        ...prev, 
        user: null, 
        role: null, 
        session: null,
        loading: false 
      }));
      
      localStorage.removeItem(USER_ID_KEY);
      localStorage.removeItem(ROLE_KEY);
      localStorage.removeItem(VIEW_AS_VIEWER_KEY);
      
      await supabase.auth.signOut();
      
      log.debug('[UserContext] logout successful');
    } catch (error) {
      log.error('Logout failed:', error);
      setState(prev => ({ 
        ...prev, 
        user: null, 
        role: null, 
        session: null,
        loading: false 
      }));
    }
  }, []);

  /**
   * Sends magic link email to user
   * Uses invite-only authentication (shouldCreateUser: false)
   * Only pre-invited users can log in via magic link
   * ❌ Prevents deactivated users from receiving magic links
   */
  const signInWithMagicLink = useCallback(async (email: string) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      
      // ✅ Check if user exists and is active BEFORE sending magic link
      // This check must work even when user is NOT logged in yet (no session)
      // Use Edge Function that uses service role (doesn't require user authentication)
      try {
        const { data: checkResult, error: checkError } = await supabase.functions.invoke('check-user-status', {
          body: { email: normalizedEmail }
          // No auth header needed - Edge Function uses service role internally
        });
        
        if (checkError) {
          logger.warn('Edge Function check-user-status error', { error: checkError, email: normalizedEmail }, 'UserContext');
          // Continue anyway - Supabase's shouldCreateUser: false will handle non-existent users
        } else if (checkResult) {
          // Check if user exists and is active
          if (!checkResult.exists) {
            // User doesn't exist
            const error = { 
              message: 'This account does not exist. Please contact an administrator.', 
              name: 'UserNotFound' 
            } as any;
            logger.warn('Magic link request for non-existent user', { email: normalizedEmail }, 'UserContext');
            return { data: null, error };
          }
          
          if (!checkResult.is_active) {
            // User exists but is deactivated
            const error = { 
              message: 'This account is deactivated. Please contact an administrator.', 
              name: 'UserDeactivated' 
            } as any;
            logger.warn('Magic link request for deactivated user', { email: normalizedEmail }, 'UserContext');
            return { data: null, error };
          }
          
          // User exists and is active - continue to send magic link
          if (import.meta.env.DEV) {
            logger.debug('User is active, proceeding with magic link', { email: normalizedEmail }, 'UserContext');
          }
        }
      } catch (checkErr) {
        // Edge Function failed - log warning but continue
        // Supabase's shouldCreateUser: false will handle non-existent users as fallback
        logger.warn('Could not verify user status via Edge Function', { email: normalizedEmail, error: checkErr }, 'UserContext');
      }
      
      // ✅ User is active (or check passed) - send magic link
      const { data, error } = await supabase.auth.signInWithOtp({
        email: normalizedEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?type=magiclink`,
          shouldCreateUser: false
        }
      });
      
      // If Supabase returns an error about user not existing, that's fine (shouldCreateUser: false)
      // But log it for debugging
      if (error && error.message?.includes('not found')) {
        logger.warn('Magic link request for non-existent user (Supabase validation)', { email: normalizedEmail }, 'UserContext');
      }
      
      return { data, error };
    } catch (error) {
      logger.error('signInWithMagicLink error', { error, email }, 'UserContext');
      return { data: null, error };
    }
  }, []);

  const fetchUserRole = useCallback(async (userId: string): Promise<{ data: { role: string } | null; error: PostgrestError | null }> => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single<{ role: string }>();
      
      return { data: data ?? null, error: error ?? null };
    } catch (error) {
      return { data: null, error: error as PostgrestError | null };
    }
  }, []);

  /**
   * Initial hydration: Loads session and role from Supabase on mount
   * Role resolution priority: JWT metadata > database query > localStorage
   */
  useEffect(() => {
    async function hydrate() {
      const { data, error } = await supabase.auth.getSession();
      if (error) log.warn('[UserContext] getSession error', error);

      const session = data?.session ?? null;
      const userId = session?.user?.id ?? null;

      // Clear viewer mode if user changed (security: prevent cross-user mode leakage)
      const lastUserId = localStorage.getItem(USER_ID_KEY);
      if (userId && lastUserId && lastUserId !== userId) {
        localStorage.removeItem(VIEW_AS_VIEWER_KEY);
      }
      if (userId) localStorage.setItem(USER_ID_KEY, userId);

      const WS_KEY = 'workspace:id';
      const DEFAULT_WS = '00000000-0000-0000-0000-000000000001';
      try {
        if (!localStorage.getItem(WS_KEY)) {
          localStorage.setItem(WS_KEY, DEFAULT_WS);
        }
      } catch (error) {
        // localStorage may be unavailable (private browsing, quota exceeded)
        if (import.meta.env.DEV) {
          logger.warn('Failed to set default workspace in localStorage', { error }, 'UserContext');
        }
      }

      let role: Role = getRoleFromSession(session);
      if (!session) {
        const stored = localStorage.getItem(ROLE_KEY);
        role = normalizeRole(stored);
      } else {
        const jwtRole = getRoleFromSession(session);
        
        if (jwtRole) {
          role = jwtRole;
          if (import.meta.env.DEV) {
            logger.debug('Using JWT role', { role }, 'UserContext');
          }
        } else {
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .maybeSingle<{ role: string }>();
          
          if (roleData) {
            role = normalizeRole(roleData.role);
            if (import.meta.env.DEV) {
              logger.debug('Database role found', { role }, 'UserContext');
            }
          } else {
            if (import.meta.env.DEV) {
              logger.debug('No role found, setting to null', undefined, 'UserContext');
            }
            role = null;
          }
        }
        localStorage.setItem(ROLE_KEY, role || '');
      }

      setState(prev => ({ ...prev, session, role, user: session?.user ? { email: session.user.email || null } : null, loading: false }));
      log.debug('[UserContext] hydrated', { userId, role, email: session?.user?.email, canEdit: role === 'admin' && viewerMode === false });
    }

    hydrate();

    /**
     * Listen for auth state changes (login, logout, token refresh)
     * Role resolution priority: JWT metadata > database query
     * Only clears localStorage on explicit SIGNED_OUT event (not token refresh)
     */
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (import.meta.env.DEV) {
        logger.debug('Auth state change', { event, hasSession: !!session, userId: session?.user?.id }, 'UserContext');
      }
      
      const userId = session?.user?.id ?? null;
      if (userId) localStorage.setItem(USER_ID_KEY, userId);

      let role: Role = session ? getRoleFromSession(session) : normalizeRole(localStorage.getItem(ROLE_KEY));
      if (session) {
        const jwtRole = getRoleFromSession(session);
        
        if (jwtRole) {
          role = jwtRole;
          if (import.meta.env.DEV) {
            logger.debug('Using JWT role', { role }, 'UserContext');
          }
        } else {
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .maybeSingle<{ role: string }>();
          
          if (roleData) {
            role = normalizeRole(roleData.role);
            if (import.meta.env.DEV) {
              logger.debug('Database role found', { role }, 'UserContext');
            }
          } else {
            if (import.meta.env.DEV) {
              logger.debug('No role found, setting to null', undefined, 'UserContext');
            }
            role = null;
          }
        }
        localStorage.setItem(ROLE_KEY, role || '');
      } else if (event === 'SIGNED_OUT') {
        localStorage.removeItem(VIEW_AS_VIEWER_KEY);
        localStorage.removeItem(USER_ID_KEY);
        localStorage.removeItem(ROLE_KEY);
      }

      setState(prev => ({ ...prev, session, role, user: session?.user ? { email: session.user.email || null } : null, loading: false }));
      log.debug('[UserContext] auth change', { event, role, email: session?.user?.email, canEdit: role === 'admin' && viewerMode === false });
    });

    return () => {
      subscription?.unsubscribe?.();
    };
  }, [viewerMode]);

  /**
   * Legacy API: Set viewer mode state
   */
  const setViewerMode = useCallback((v: boolean) => {
    setViewerModeState(v);
    try {
      localStorage.setItem('ui:viewerMode', String(v));
    } catch (error) {
      logger.error('Failed to set viewer mode in localStorage', { error }, 'UserContext');
    }
  }, []);

  const userMode = viewerMode ? 'viewer' : 'admin';
  
  const switchUserMode = useCallback((mode: 'admin' | 'viewer') => {
    const newViewerMode = mode === 'viewer';
    setViewerMode(newViewerMode);
  }, [setViewerMode]);

  const viewerId = useMemo(() => {
    return `viewer-${Math.random().toString(36).substr(2, 9)}-${Date.now()}`;
  }, []);

  // TEMPORARY: Smoke test mode - provide default values
  const SMOKE_TEST_MODE = import.meta.env.VITE_SMOKE_TEST === 'true';
  const smokeTestUser = SMOKE_TEST_MODE ? { email: 'smoke-test@example.com' } : null;
  const smokeTestRole = SMOKE_TEST_MODE ? 'admin' : null;
  
  const workspaceId = state.session ? getWorkspaceIdFromSession(state.session) : '00000000-0000-0000-0000-000000000001';
  
  const value: UserContextType = {
    user: SMOKE_TEST_MODE ? smokeTestUser : state.user,
    role: SMOKE_TEST_MODE ? (smokeTestRole as Role) : role,
    isAdmin: SMOKE_TEST_MODE ? true : isAdmin,
    isActuallyAdmin: SMOKE_TEST_MODE ? true : isActuallyAdmin,
    viewerMode: SMOKE_TEST_MODE ? false : viewerMode,
    canEdit: SMOKE_TEST_MODE ? true : canEdit,
    workspaceId,
    // authReady: true when loading is complete (either user authenticated or not)
    // This prevents flash of login modal during initial auth check
    // In smoke test mode, always ready
    authReady: SMOKE_TEST_MODE ? true : !state.loading,
    userId: SMOKE_TEST_MODE ? 'smoke-test-user-id' : (state.session?.user?.id ?? null),
    viewAsViewer,
    backToAdmin,
    canEditSection,
    login,
    logout,
    // New authentication methods
    signInWithMagicLink,
    fetchUserRole,
    // Legacy API compatibility
    setViewerMode,
    userMode,
    switchUserMode,
    viewerId,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within UserProvider');
  return ctx;
};
